import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActions, DialogTitle, DialogContent, Button, Paper, Tabs, Tab, AppBar } from '@material-ui/core';
import { IData, IAgreementListItem, IDeviceListItem, IItSystemListItem, ILicenseListItem, IPersonListItem, IRelatedAgreementListItem, IRelatedDeviceListItem, IRelatedLicenseListItem, parseToAgreement, parseToDevice, parseToLicense, IServiceListItem } from './IModels';
import { makeid } from '../../utils/randomHelper';
import { GridTabPanel } from '../GridHelper/GridTabPanel';
import ShowResourcesGrid from './ShowResourcesGrid';
import { transformIcon } from './LogPlusIconWrapper';

type SupportedTabKey = "GetAllData" | "GetRelatedData";

export type OnSaveResult = IDeviceListItem[] | IItSystemListItem[] | IPersonListItem[] | ILicenseListItem[] | IAgreementListItem[] | IServiceListItem[];

interface ISearchDialogProps {
	kinds: (keyof IData)[],

	showGridTabs: string,

	filterId: string,
	relatedFilterId: string,
	notifier: { value: string, label: string },

	gridColumns: any,
	relatedGridColumns: any,
	
	onSave: (r: OnSaveResult) => void,
	onClose: () => void,
	manageFiltersPermission: boolean,
}

export default function SearchDialog(props: ISearchDialogProps) {
	const { t } = useTranslation();
	const [currentTabIndex, setCurrentTabIndex] = React.useState(0);

	const [gridTempId, setGridIdValue] = React.useState("")

	React.useEffect(() => {
		setGridIdValue(makeid(5));
	}, [open])

	if (!props.showGridTabs) {
		console.warn("Required property \'showGridTabs\'.");
	}

	let tabs = [];
	if (props.showGridTabs) {
		tabs = props.showGridTabs.split(',')
			.map((tabKey: SupportedTabKey) => {

				let mainKind;
				switch (props.kinds[0]) {
					case "Person":
					case "ItSystem":
					case "Agreement":
					case "License":
					case "Service":
						mainKind = props.kinds[0];
						break;
					default:
						mainKind = "Device";
						break;
				}

				return {
					key: tabKey,
					label: t("logPlusResources." + tabKey + "_" + mainKind)
				}
			});
	}
	

	const currentTab: SupportedTabKey
		= tabs[currentTabIndex]?.key;

	const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
		setCurrentTabIndex(newValue);
	};

	const handleSave = () => {
		function getPropertyValue<T>(model, data, property: keyof T): any {
			if (model[property]) {
				return data[model[property].index];
			}

			return null;
		}

		function getFocusedData<T>(gridKey: SupportedTabKey, fnMapItSystem: (model, data) => T): T[] {
			let grid = (window as any).$('#' + gridTempId + "-grid-" + gridKey).dataTable({ bRetrieve: true });
			var rows = (window as any).fnGetSelected(grid);
			if (rows == null || rows.length === 0) return null;
			var aReturn = [];
			for (var i = 0; i < rows.length; i++) {
				var data = grid.fnGetData(rows[i]);
				var model = grid.fnGetModelData(rows[i]);

				var item = fnMapItSystem(model, data);

				aReturn.push(item);
			}
			return aReturn;
		}

		function mapItSystem(model, data): IItSystemListItem {
			var item: IItSystemListItem = {
				Id: data[model.Id.index],
				Name: data[model.Name.index],
				Type: data[model.Type.index],

				Description: getPropertyValue<IItSystemListItem>(model, data, "Description"),
				EnvironmentType: getPropertyValue<IItSystemListItem>(model, data, "EnvironmentType"),
				Status: getPropertyValue<IItSystemListItem>(model, data, "Status"),
			};

			return item;
		}

		function mapService(model, data): IServiceListItem {
			var item: IServiceListItem = {
				Id: data[model.Id.index],
				Name: data[model.Name.index],

				Icon: getPropertyValue<IServiceListItem>(model, data, "Icon"),
				CommercialDescription: getPropertyValue<IServiceListItem>(model, data, "CommercialDescription"),
				Status: getPropertyValue<IServiceListItem>(model, data, "Status"),
				OwnerId: getPropertyValue<IServiceListItem>(model, data, "OwnerId"),
				OwnerName: getPropertyValue<IServiceListItem>(model, data, "OwnerName"),
				OwnId: getPropertyValue<IServiceListItem>(model, data, "OwnId"),
				ServiceType: getPropertyValue<IServiceListItem>(model, data, "ServiceType"),
				Validity: getPropertyValue<IServiceListItem>(model, data, "Validity"),
				ServiceProfileGroupId: getPropertyValue<IServiceListItem>(model, data, "ServiceProfileGroupId"),
				ServiceProfileGroupName: getPropertyValue<IServiceListItem>(model, data, "ServiceProfileGroupName"),
				ServiceProfileId: getPropertyValue<IServiceListItem>(model, data, "ServiceProfileId"),
				ServiceProfileName: getPropertyValue<IServiceListItem>(model, data, "ServiceProfileName"),
			};

			return item;
		}

		function mapAgreement(model, data): IAgreementListItem {
			var item: IAgreementListItem = {
				Id: data[model.Id.index],
				Name: data[model.Name.index],
				Type: data[model.Type.index],

				Description: getPropertyValue<IAgreementListItem>(model, data, "Description"),
				Status: getPropertyValue<IAgreementListItem>(model, data, "Status"),
				AgreementNumber: getPropertyValue<IAgreementListItem>(model, data, "AgreementNumber"),
				ValidFrom: getPropertyValue<IAgreementListItem>(model, data, "ValidFrom"),
				ValidTo: getPropertyValue<IAgreementListItem>(model, data, "ValidTo"),
			};

			return item;
		}

		function mapRelatedAgreement(model, data): IAgreementListItem {
			var item: IRelatedAgreementListItem = {
				Id: data[model.Id.index],
				Name: data[model.Name.index],
				Type: getPropertyValue<IRelatedAgreementListItem>(model, data, "Type"),
				Status: getPropertyValue<IRelatedAgreementListItem>(model, data, "Status"),
				AgreementNumber: getPropertyValue<IRelatedAgreementListItem>(model, data, "AgreementNumber"),
				Description: getPropertyValue<IRelatedAgreementListItem>(model, data, "Description"),
				ValidFrom: getPropertyValue<IRelatedAgreementListItem>(model, data, "ValidFrom"),
				ValidTo: getPropertyValue<IRelatedAgreementListItem>(model, data, "ValidTo")
			};

			return parseToAgreement(item);
		}

		function mapLicense(model, data): ILicenseListItem {
			var item: ILicenseListItem = {
				Id: data[model.Id.index],
				Name: data[model.Name.index],
				LicenseType: data[model.LicenseType.index],
				Description: getPropertyValue<ILicenseListItem>(model, data, "Description"),
				Status: getPropertyValue<ILicenseListItem>(model, data, "Status"),
				LicenseTypeDescription: getPropertyValue<ILicenseListItem>(model, data, "LicenseTypeDescription"),
			};

			return item;
		}

		function mapRelatedLicense(model, data): ILicenseListItem {
			var item: IRelatedLicenseListItem = {
				Id: data[model.Id.index],
				Product: getPropertyValue<IRelatedLicenseListItem>(model, data, "Product"),
				Description: getPropertyValue<IRelatedLicenseListItem>(model, data, "Description"),
				Status: getPropertyValue<IRelatedLicenseListItem>(model, data, "Status"),
				LicenseType: getPropertyValue<IRelatedLicenseListItem>(model, data, "LicenseType"),
				LicenseTypeDescription: getPropertyValue<IRelatedLicenseListItem>(model, data, "LicenseTypeDescription")
			};

			return parseToLicense(item);
		}


		function mapPerson(model, data): IPersonListItem {
			var item: IPersonListItem = {
				Id: data[model.Id.index],
				Login: data[model.Login.index],
				Cellphone: getPropertyValue<IPersonListItem>(model, data, "Cellphone"),
				Company: getPropertyValue<IPersonListItem>(model, data, "Company"),
				Email: getPropertyValue<IPersonListItem>(model, data, "Email"),
				Status: getPropertyValue<IPersonListItem>(model, data, "Status"),
				FirstName: getPropertyValue<IPersonListItem>(model, data, "FirstName"),
				JobPosition: getPropertyValue<IPersonListItem>(model, data, "JobPosition"),
				LastName: getPropertyValue<IPersonListItem>(model, data, "LastName"),
				MpkNumber: getPropertyValue<IPersonListItem>(model, data, "MpkNumber"),
				SapId: getPropertyValue<IPersonListItem>(model, data, "SapId"),
				SupervisorId: getPropertyValue<IPersonListItem>(model, data, "SupervisorId"),
				SupervisorName: getPropertyValue<IPersonListItem>(model, data, "SupervisorName"),
				UserName: getPropertyValue<IPersonListItem>(model, data, "UserName"),
				WorkPhone: getPropertyValue<IPersonListItem>(model, data, "WorkPhone"),
			};

			return item;
		}

		function mapDevice(model, data): IDeviceListItem {
			var item: IDeviceListItem = {
				Id: data[model.Id.index],
				Name: data[model.Name.index],
				Type: data[model.Type.index],
				Description: getPropertyValue<IDeviceListItem>(model, data, "Description"),
				Status: getPropertyValue<IDeviceListItem>(model, data, "Status"),
				HdKind: getPropertyValue<IDeviceListItem>(model, data, "HdKind"),
				InventoryNumber: getPropertyValue<IDeviceListItem>(model, data, "InventoryNumber"),
				Model: getPropertyValue<IDeviceListItem>(model, data, "Model"),
				SerialNumber: getPropertyValue<IDeviceListItem>(model, data, "SerialNumber"),
			};

			return item;
		}

		function mapRelatedDevice(model, data): IDeviceListItem {
			var item: IRelatedDeviceListItem = {
				AssetId: data[model.AssetId.index],
				Name: data[model.Name.index],
				Description: getPropertyValue<IRelatedDeviceListItem>(model, data, "Description"),
				AssetKind: getPropertyValue<IRelatedDeviceListItem>(model, data, "AssetKind"),
				Status: getPropertyValue<IRelatedDeviceListItem>(model, data, "Status"),
				HdKind: getPropertyValue<IRelatedDeviceListItem>(model, data, "HdKind"),
				InventoryNumber: getPropertyValue<IRelatedDeviceListItem>(model, data, "InventoryNumber"),
				Model: getPropertyValue<IRelatedDeviceListItem>(model, data, "Model"),
				SerialNumber: getPropertyValue<IRelatedDeviceListItem>(model, data, "SerialNumber")
			};

			return parseToDevice(item);
		}

		function getRowsFromFocusedGrid() {

			switch (props.kinds[0]) {
				case "Person":
					return getFocusedData(currentTab, mapPerson);
				case "ItSystem":
					return getFocusedData(currentTab, mapItSystem);
				case "Service":
					return getFocusedData(currentTab, mapService);
				case "Agreement":
					if (currentTab == "GetRelatedData")
						return getFocusedData(currentTab, mapRelatedAgreement);
					return getFocusedData(currentTab, mapAgreement);
				case "License":
					if (currentTab == "GetRelatedData")
						return getFocusedData(currentTab, mapRelatedLicense);
					return getFocusedData(currentTab, mapLicense);
			}

			switch (currentTab) {
				case "GetRelatedData":
					return getFocusedData(currentTab, mapRelatedDevice);
				case "GetAllData":
					return getFocusedData(currentTab, mapDevice);
			}
		}

		var rows = getRowsFromFocusedGrid();

		props.onClose();
		props.onSave(rows);
	}

	return (<Dialog
		open={true}
		onClose={props.onClose}
		fullWidth
		maxWidth="lg"
	>
		<DialogTitle>{t("common.action.select")}: {tabs[currentTabIndex]?.label}</DialogTitle>
		<DialogContent>
			<Paper square>
				{tabs.length > 1 &&
					<AppBar position="static" color="default">
						<Tabs
							value={currentTabIndex}
							indicatorColor="primary"
							textColor="primary"
							onChange={handleChange}
						>
							{tabs.map(x => <Tab key={x.key} label={t(x.label)} />)}
						</Tabs>
					</AppBar>}

				<GridTabPanel value={currentTab} index={"GetRelatedData"}>
					<ShowRelatedResourcesGrid
						filterId={props.relatedFilterId}
						gridId={gridTempId + "-grid-GetRelatedData"}
						gridColumns={props.relatedGridColumns}
						kinds={props.kinds}
						manageFiltersPermission={props.manageFiltersPermission}
						notifier={props.notifier}
					/>
				</GridTabPanel>
				<GridTabPanel value={currentTab} index={"GetAllData"}>
					<ShowAllResourcesGrid
						filterId={props.filterId}
						gridId={gridTempId + "-grid-GetAllData"}
						gridColumns={props.gridColumns}
						kinds={props.kinds}
						manageFiltersPermission={props.manageFiltersPermission}
						notifier={props.notifier}
					/>
				</GridTabPanel>
			</Paper>
		</DialogContent>
		<DialogActions>
			<Button onClick={props.onClose} variant="contained" color="default">
				{t("common.action.cancel")}
			</Button>
			<Button onClick={handleSave} color="primary" variant="contained" autoFocus>
				{t("common.action.ok")}
			</Button>
		</DialogActions>
	</Dialog>)
}

interface IShowAllResourcesGridProps {
	kinds: (keyof IData)[],

	filterId: string,
	notifier: { value: string, label: string },

	gridColumns: any,

	manageFiltersPermission: boolean,

	gridId: string,
}

function ShowAllResourcesGrid(props: IShowAllResourcesGridProps) {

	const filterId = !!props.filterId ? "filterId=" + encodeURIComponent(props.filterId) : "";

	switch (props.kinds[0]) {
		case "Person": return (<ShowResourcesGrid
			gridId={props.gridId}
			ajaxSource={"/LogPlusResources/GetPersonsData/?" + filterId}
			gridColumns={props.gridColumns}
			manageFiltersPermission={props.manageFiltersPermission}
			filterModel="PersonListItem"
			idIndex={1}
			hdKindIndex="Person"
			checkboxClassName="react-checkbox-portal-container"
		/>);

		case "ItSystem": return (<ShowResourcesGrid
			gridId={props.gridId}
			ajaxSource={`/LogPlusResources/GetItSystemsData/?${filterId}`}
			gridColumns={props.gridColumns}
			manageFiltersPermission={props.manageFiltersPermission}
			filterModel="ItSystemListItem"

			idIndex={1}
			hdKindIndex="ItSystem"
			checkboxClassName="react-checkbox-portal-container"
		/>);

		case "Service": return (<ShowResourcesGrid
			gridId={props.gridId}
			ajaxSource={`/LogPlusResources/GetServicesData/?${filterId}`}
			gridColumns={props.gridColumns}
			manageFiltersPermission={props.manageFiltersPermission}
			filterModel="ServiceListItem"

			idIndex={1}
			hdKindIndex="Service"
			checkboxClassName="react-checkbox-portal-container"

			onRowCallback={(nRow, aData, iDisplayIndex, data) => {
				if (!!data.Icon && data.Icon.value) {
					let iconCell = (window as any).$('td:eq(' + data.Icon.visibleIndex + ')', nRow);
					if (iconCell[0]) {
						iconCell.html('<i class="material-icons">' + transformIcon(data.Icon.value) + '</i>');
					}
				}
			}}
		/>);

		case "Agreement": return (<ShowResourcesGrid
			gridId={props.gridId}
			ajaxSource={`/LogPlusResources/GetAgreementsData/?${filterId}`}
			gridColumns={props.gridColumns}
			manageFiltersPermission={props.manageFiltersPermission}
			filterModel="AgreementListItem"

			idIndex={1}
			hdKindIndex="Agreement"
			checkboxClassName="react-checkbox-portal-container"
		/>)

		case "License": return (<ShowResourcesGrid
			gridId={props.gridId}
			ajaxSource={`/LogPlusResources/GetLicensesData/?${filterId}`}
			gridColumns={props.gridColumns}
			manageFiltersPermission={props.manageFiltersPermission}
			filterModel="LicenseListItem"

			idIndex={1}
			hdKindIndex="License"
			checkboxClassName="react-checkbox-portal-container"
		/>)
	}
	return <ShowResourcesGrid
		gridId={props.gridId}
		ajaxSource={`/LogPlusResources/GetDevicesData/?kinds=${encodeURIComponent(props.kinds.join(','))}&${filterId}`}
		gridColumns={props.gridColumns}
		manageFiltersPermission={props.manageFiltersPermission}
		filterModel="BaseDeviceListItem"
		idIndex={1}
		hdKindIndex={2}
		checkboxClassName="react-checkbox-portal-container"
	/>
}

function ShowRelatedResourcesGrid(props: IShowAllResourcesGridProps) {

	const filterId = !!props.filterId ? "filterId=" + encodeURIComponent(props.filterId) : "";

	switch (props.kinds[0]) {
		
		case "Agreement": return (<ShowResourcesGrid
			gridId={props.gridId}
			ajaxSource={`/LogPlusResources/GetRelatedAgreementsData/?notifierId=${encodeURIComponent(props.notifier?.value)}&${filterId}`}
			gridColumns={props.gridColumns}
			manageFiltersPermission={props.manageFiltersPermission}
			filterModel="RelatedAgreementListItem"

			idIndex={1}
			hdKindIndex="Agreement"
			checkboxClassName="react-checkbox-portal-container"
		/>)

		case "License": return (<ShowResourcesGrid
			gridId={props.gridId}
			ajaxSource={`/LogPlusResources/GetRelatedLicensesData/?notifierId=${encodeURIComponent(props.notifier?.value)}&${filterId}`}
			gridColumns={props.gridColumns}
			manageFiltersPermission={props.manageFiltersPermission}
			filterModel="RelatedLicenseListItem"

			idIndex={1}
			hdKindIndex="License"
			checkboxClassName="react-checkbox-portal-container"
		/>)
	}

	return <ShowResourcesGrid
		gridId={props.gridId}
		ajaxSource={`/LogPlusResources/GetRelatedDevicesData/?notifierId=${encodeURIComponent(props.notifier?.value)}&kinds=${encodeURIComponent(props.kinds.join(','))}&${filterId}`}
		gridColumns={props.gridColumns}
		manageFiltersPermission={props.manageFiltersPermission}
		filterModel="RelatedDeviceListItem"
		idIndex={1}
		hdKindIndex={2}
		checkboxClassName="react-checkbox-portal-container"
	/>
}